/**
 * BPM Service - Heart Rate Monitoring
 * Integrates with HealthKit (iOS), Google Fit (Android), and BLE sensors
 */

import { Capacitor } from '@capacitor/core';
import { BleClient, BleDevice } from '@capacitor-community/bluetooth-le';
import { CapacitorHealthkit } from '@perfood/capacitor-healthkit';
import type { QueryOutput, SampleNames } from '@perfood/capacitor-healthkit';
import { logger } from '@/lib/logger';
import { persistentLogger } from '@/lib/persistent-logger';

interface BPMReading {
  bpm: number;
  timestamp: number;
  source: 'healthkit' | 'googlefit' | 'ble' | 'manual';
  deviceId?: string;
}

interface BPMZone {
  name: string;
  min: number;
  max: number;
  color: string;
}

interface BLEDebugLog {
  timestamp: number;
  level: 'info' | 'warn' | 'error';
  message: string;
  data?: any;
}

export interface DiscoveredBLEDevice extends BleDevice {
  rssi?: number;
  hasHRService?: boolean;
  isSamsung?: boolean;
  isWearable?: boolean;
}

class BPMService {
  private currentBPM: number = 0;
  private readings: BPMReading[] = [];
  private isMonitoring = false;
  private bleDevice: BleDevice | null = null;
  private healthKitAvailable = false;
  private googleFitAvailable = false;
  private statusCallbacks: Array<(status: string) => void> = [];
  private bpmCallbacks: Array<(reading: BPMReading) => void> = [];
  private devicesFoundCallbacks: Array<(devices: DiscoveredBLEDevice[]) => void> = [];
  private healthKitInterval: NodeJS.Timeout | null = null;
  private initPromise: Promise<void> | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectTimeout: NodeJS.Timeout | null = null;
  private connectionLostCallback: (() => void) | null = null;
  private debugLogs: BLEDebugLog[] = [];
  private maxDebugLogs = 100;
  private discoveredDevices: DiscoveredBLEDevice[] = [];

  // Heart Rate Service UUID (standard BLE)
  private readonly HEART_RATE_SERVICE = '0000180d-0000-1000-8000-00805f9b34fb';
  private readonly HEART_RATE_CHARACTERISTIC = '00002a37-0000-1000-8000-00805f9b34fb';

  constructor() {
    this.initPromise = this.initialize();
  }
  
  /**
   * Wait for initialization to complete (useful for tests)
   */
  async waitForInitialization(): Promise<void> {
    await this.initPromise;
  }
  
  /**
   * Register callback for status changes (scanning, connecting, connected, error, etc.)
   * Returns unsubscribe function
   */
  onStatusChange(callback: (status: string) => void): () => void {
    this.statusCallbacks.push(callback);
    return () => {
      this.statusCallbacks = this.statusCallbacks.filter(cb => cb !== callback);
    };
  }

  /**
   * Register callback for real-time BPM readings
   * Returns unsubscribe function
   */
  onBPMReading(callback: (reading: BPMReading) => void): () => void {
    this.bpmCallbacks.push(callback);
    return () => {
      this.bpmCallbacks = this.bpmCallbacks.filter(cb => cb !== callback);
    };
  }

  /**
   * Register callback for connection loss (triggers auto-reconnect)
   */
  onConnectionLost(callback: () => void) {
    this.connectionLostCallback = callback;
  }

  /**
   * Register callback for when devices are discovered during scan
   * Returns unsubscribe function
   */
  onDevicesFound(callback: (devices: DiscoveredBLEDevice[]) => void): () => void {
    this.devicesFoundCallbacks.push(callback);
    return () => {
      this.devicesFoundCallbacks = this.devicesFoundCallbacks.filter(cb => cb !== callback);
    };
  }

  /**
   * Emit discovered devices to all registered callbacks
   */
  private emitDevicesFound(devices: DiscoveredBLEDevice[]) {
    this.devicesFoundCallbacks.forEach(cb => cb(devices));
  }

  /**
   * Get currently discovered devices
   */
  getDiscoveredDevices(): DiscoveredBLEDevice[] {
    return [...this.discoveredDevices];
  }

  /**
   * Clear discovered devices list
   */
  clearDiscoveredDevices() {
    this.discoveredDevices = [];
    this.emitDevicesFound([]);
  }

  /**
   * Connect to a specific device by deviceId (for manual selection)
   */
  async connectToSpecificDevice(device: DiscoveredBLEDevice): Promise<boolean> {
    try {
      this.addDebugLog('info', `Manual connection to ${device.name}`);
      this.emitStatus('connecting');

      const connected = await this.connectToDevice(device);
      
      if (connected) {
        this.addDebugLog('info', `✅ Manual connection successful: ${device.name}`);
        this.trackEvent('ble_manual_connection', { deviceName: device.name });
      }

      return connected;
    } catch (error) {
      this.addDebugLog('error', `❌ Manual connection failed: ${error}`);
      return false;
    }
  }

  /**
   * Add debug log entry (for real-time debugging)
   */
  private addDebugLog(level: 'info' | 'warn' | 'error', message: string, data?: any) {
    // Serialize Error objects correctly
    const serializedData = data ? this.serializeErrorData(data) : undefined;
    
    const log: BLEDebugLog = {
      timestamp: Date.now(),
      level,
      message,
      data: serializedData
    };
    
    this.debugLogs.push(log);
    
    // Keep only last 100 logs
    if (this.debugLogs.length > this.maxDebugLogs) {
      this.debugLogs.shift();
    }
    
    // Save to localStorage for persistence
    try {
      localStorage.setItem('ble_debug_logs', JSON.stringify(this.debugLogs));
    } catch (e) {
      // Ignore storage errors
    }
    
    // Emit as status update for UI
    this.emitStatus(`debug:${level}:${message}`);
  }

  /**
   * Serialize Error objects and nested data for proper logging
   */
  private serializeErrorData(data: any): any {
    if (data === null || data === undefined) return data;
    
    // Handle Error objects
    if (data instanceof Error) {
      return {
        name: data.name,
        message: data.message,
        stack: data.stack
      };
    }
    
    // Handle plain objects recursively
    if (typeof data === 'object' && !Array.isArray(data)) {
      const result: any = {};
      for (const key of Object.keys(data)) {
        result[key] = this.serializeErrorData(data[key]);
      }
      return result;
    }
    
    // Handle arrays
    if (Array.isArray(data)) {
      return data.map(item => this.serializeErrorData(item));
    }
    
    return data;
  }

  /**
   * Get debug logs for troubleshooting
   */
  getDebugLogs(): BLEDebugLog[] {
    return [...this.debugLogs];
  }

  /**
   * Clear debug logs
   */
  clearDebugLogs() {
    this.debugLogs = [];
    localStorage.removeItem('ble_debug_logs');
  }
  
  private emitStatus(status: string) {
    this.statusCallbacks.forEach(cb => cb(status));
  }

  private async initialize() {
    const platform = Capacitor.getPlatform();

    this.addDebugLog('info', 'Initializing BPM Service', { platform });

    // Check BLE availability
    try {
      await BleClient.initialize();
      logger.info('BLE initialized', { platform });
      this.addDebugLog('info', 'BLE Client initialized successfully');
    } catch (error) {
      logger.warn('BLE not available', { platform, error });
      this.addDebugLog('error', 'BLE initialization failed', { error });
    }

    // Check HealthKit (iOS)
    if (platform === 'ios') {
      try {
        const isAvailable = await CapacitorHealthkit.isAvailable();
        this.healthKitAvailable = isAvailable.isAvailable;
        
        if (this.healthKitAvailable) {
          logger.info('HealthKit available on device');
          this.addDebugLog('info', 'HealthKit is available on this device');
        } else {
          this.addDebugLog('warn', 'HealthKit is not available on this device');
        }
      } catch (error: any) {
        // Capture detailed error information
        const errorDetails = {
          name: error?.name || 'Unknown',
          message: error?.message || String(error),
          code: error?.code,
          stack: error?.stack?.split('\n').slice(0, 3).join('\n')
        };
        
        logger.warn('HealthKit check failed', errorDetails);
        this.healthKitAvailable = false;
        this.addDebugLog('error', `HealthKit check failed: ${errorDetails.message}`, errorDetails);
        
        // Add to persistent logger for future debugging
        persistentLogger.addLog('HEALTHKIT', 'error', 'HealthKit check failed', errorDetails);
        
        // HealthKit failure is non-blocking - BLE monitoring still available
        this.addDebugLog('warn', '⚠️ HealthKit unavailable - BLE monitoring still available');
        logger.info('Continuing initialization - BLE monitoring available as fallback');
      }
    }

    // Check Google Fit (Android)
    if (platform === 'android') {
      // TODO: Add Google Fit plugin integration
      // this.googleFitAvailable = await GoogleFit.isAvailable();
    }
  }

  /**
   * Scan for BLE devices without connecting (for manual device selection)
   * Returns promise that resolves when scan completes
   */
  async scanOnly(duration: number = 30000): Promise<DiscoveredBLEDevice[]> {
    try {
      this.addDebugLog('info', `Starting scan-only mode for ${duration}ms`);
      this.discoveredDevices = [];
      this.emitStatus('scanning');
      
      // Check Bluetooth state
      const btState = await BleClient.isEnabled();
      if (!btState) {
        this.addDebugLog('error', 'Bluetooth is disabled');
        this.emitStatus('error_bluetooth_disabled');
        throw new Error('Bluetooth está desativado');
      }
      
      // Re-initialize BLE Client
      await BleClient.initialize();
      this.addDebugLog('info', 'BLE Client initialized for scan');
      
      // Start unfiltered scan - show ALL devices
      await BleClient.requestLEScan(
        {}, // No filter - all BLE devices
        (result) => {
          const deviceInfo = `${result.device.name || 'Unknown'} | RSSI: ${result.rssi}dB`;
          this.addDebugLog('info', `Device found: ${deviceInfo}`);
          
          // Add device if not duplicate
          if (!this.discoveredDevices.some(d => d.deviceId === result.device.deviceId)) {
            const name = (result.device.name || '').toLowerCase();
            const isWearable = name.includes('gear') || name.includes('galaxy') || 
                              name.includes('watch') || name.includes('band') ||
                              name.includes('polar') || name.includes('garmin') ||
                              name.includes('suunto') || name.includes('samsung') ||
                              name.includes('sm-') || name.includes('fit') || 
                              name.includes('active') || name.includes('apple');
            
            const discoveredDevice: DiscoveredBLEDevice = {
              ...result.device,
              rssi: result.rssi,
              hasHRService: undefined,
              isSamsung: this.isSamsungDevice(result.device.name || ''),
              isWearable
            };
            
            this.discoveredDevices.push(discoveredDevice);
            this.emitDevicesFound([...this.discoveredDevices]);
          }
        }
      );
      
      // Wait for scan duration
      await new Promise(resolve => setTimeout(resolve, duration));
      await BleClient.stopLEScan();
      
      this.addDebugLog('info', `Scan complete: ${this.discoveredDevices.length} devices found`);
      this.emitStatus('scan_complete');
      
      return [...this.discoveredDevices];
    } catch (error) {
      this.addDebugLog('error', `Scan failed: ${error}`);
      this.emitStatus('error');
      throw error;
    }
  }

  async startMonitoring(sessionId: string): Promise<void> {
    // Ensure initialization is complete before starting
    await this.waitForInitialization();
    
    if (this.isMonitoring) return;

    this.isMonitoring = true;
    this.readings = [];
    this.emitStatus('scanning');

    // Auto-stop após 30 segundos se não conectar
    const timeoutId = setTimeout(() => {
      if (this.bleDevice === null && this.isMonitoring) {
        logger.info('BLE scan timeout - stopping automatically');
        this.emitStatus('idle');
        this.isMonitoring = false;
      }
    }, 30000);

    // ========================================
    // NATIVE-FIRST STRATEGY
    // Priority order:
    // 1. HealthKit (iOS) / Google Fit (Android) - Native platform APIs
    // 2. Already connected BLE devices (check system connections)
    // 3. BLE scan for new devices
    // ========================================

    // Layer -1: Try native health platforms FIRST (most reliable for watches)
    const nativeConnected = await this.startHealthPlatformMonitoring();
    
    if (nativeConnected) {
      clearTimeout(timeoutId);
      this.emitStatus('connected');
      logger.info('✅ Connected via native health platform (HealthKit/Google Fit)');
      this.trackEvent('bpm_monitoring_started', { sessionId, source: 'native_health' });
      return;
    }

    // Layer 0: Check for already connected BLE devices
    const existingDevice = await this.checkConnectedDevices();
    
    if (existingDevice) {
      clearTimeout(timeoutId);
      logger.info('✅ Using existing BLE connection');
      this.trackEvent('bpm_monitoring_started', { sessionId, source: 'existing_ble' });
      return;
    }

    // Layer 1-3: Full BLE scan as fallback
    const bleConnected = await this.connectBLESensor();
    clearTimeout(timeoutId);
    
    if (!bleConnected) {
      this.emitStatus('error');
      logger.warn('Failed to connect to any BPM source');
    }

    this.trackEvent('bpm_monitoring_started', { 
      sessionId,
      source: bleConnected ? 'ble_scan' : 'none' 
    });
  }

  async stopMonitoring(): Promise<BPMReading[]> {
    this.isMonitoring = false;

    if (this.bleDevice) {
      await this.disconnectBLESensor();
    }

    await this.stopHealthPlatformMonitoring();

    const finalReadings = [...this.readings];
    this.readings = [];

    this.trackEvent('bpm_monitoring_stopped', {
      totalReadings: finalReadings.length
    });

    return finalReadings;
  }

  /**
   * Check for already connected BLE devices (e.g., watches paired with phone)
   * Layer 0: Before scanning, check system connections
   */
  private async checkConnectedDevices(): Promise<boolean> {
    try {
      logger.info('[Connected Devices] Checking for existing BLE connections');
      persistentLogger.addLog('BLE', 'info', 'Checking connected devices', {});

      // Get devices already connected with HR service
      const connectedDevices = await BleClient.getConnectedDevices([this.HEART_RATE_SERVICE]);
      
      if (connectedDevices.length > 0) {
        const device = connectedDevices[0];
        logger.info('[Connected Devices] Found existing HR device', { 
          name: device.name,
          deviceId: device.deviceId 
        });
        persistentLogger.addLog('BLE', 'info', 'Found connected device', { name: device.name });

        // Use this device directly
        this.bleDevice = device;
        
        // Start notifications
        await BleClient.startNotifications(
          device.deviceId,
          this.HEART_RATE_SERVICE,
          this.HEART_RATE_CHARACTERISTIC,
          (value) => this.handleBLEHeartRate(value)
        );
        
        // Save device info
        localStorage.setItem('ble_hr_device_id', device.deviceId);
        localStorage.setItem('ble_hr_device_name', device.name || 'Connected Device');
        
        this.emitStatus('connected');
        this.trackEvent('ble_sensor_connected', { deviceName: device.name, layer: -1 });
        
        return true;
      }

      logger.info('[Connected Devices] No existing HR connections found');
      return false;
    } catch (error) {
      logger.warn('[Connected Devices] Failed to check connected devices', { error });
      persistentLogger.addLog('BLE', 'warn', 'getConnectedDevices failed', { error });
      return false;
    }
  }

  private async connectBLESensor(): Promise<boolean> {
    try {
      this.addDebugLog('info', 'Starting BLE sensor connection process');
      
      // Check Bluetooth system state first
      try {
        const btState = await BleClient.isEnabled();
        this.addDebugLog('info', 'Bluetooth state checked', { enabled: btState });
        
        if (!btState) {
          this.addDebugLog('error', 'Bluetooth is disabled on device');
          this.emitStatus('error_bluetooth_disabled');
          return false;
        }
      } catch (error) {
        this.addDebugLog('warn', 'Could not check Bluetooth state', { error });
      }
      
      // Re-initialize BleClient to ensure it's ready
      await BleClient.initialize();
      this.addDebugLog('info', '✅ BLE Client re-initialized');
      logger.info('🔵 BLE Client re-initialized', { platform: Capacitor.getPlatform() });
      
      this.emitStatus('scanning');
      
      // ==== LAYER 0 (NEW): Direct connection attempt to saved device ====
      const savedDeviceId = localStorage.getItem('ble_hr_device_id');
      const savedDeviceName = localStorage.getItem('ble_hr_device_name');
      
      if (savedDeviceId && savedDeviceName) {
        logger.info('[Layer 0] Attempting direct connection to saved device', { 
          deviceId: savedDeviceId, 
          deviceName: savedDeviceName 
        });
        persistentLogger.addLog('BLE', 'info', 'Layer 0: Direct connect', { deviceName: savedDeviceName });
        this.addDebugLog('info', `[Layer 0] 🔗 Direct connect: ${savedDeviceName}`);
        
        try {
          this.emitStatus('connecting');
          await BleClient.connect(savedDeviceId, (deviceId) => {
            logger.warn('Device disconnected unexpectedly', { deviceId });
            this.attemptReconnect();
          });
          
          // Verify HR service exists
          const services = await BleClient.getServices(savedDeviceId);
          const hasHRService = services.some(s => 
            s.uuid.toLowerCase() === this.HEART_RATE_SERVICE.toLowerCase()
          );
          
          if (hasHRService) {
            logger.info('✅ [Layer 0] Direct connection successful!');
            persistentLogger.addLog('BLE', 'info', 'Layer 0: Success', { deviceName: savedDeviceName });
            this.addDebugLog('info', `[Layer 0] ✅ Connected to ${savedDeviceName}`);
            
            this.bleDevice = { deviceId: savedDeviceId, name: savedDeviceName };
            
            await BleClient.startNotifications(
              savedDeviceId,
              this.HEART_RATE_SERVICE,
              this.HEART_RATE_CHARACTERISTIC,
              (value) => this.handleBLEHeartRate(value)
            );
            
            this.emitStatus('connected');
            this.trackEvent('ble_sensor_connected', { deviceName: savedDeviceName, layer: 0 });
            return true;
          } else {
            logger.warn('[Layer 0] Device has no HR service');
            persistentLogger.addLog('BLE', 'warn', 'Layer 0: No HR service', {});
            await BleClient.disconnect(savedDeviceId);
            localStorage.removeItem('ble_hr_device_id');
            localStorage.removeItem('ble_hr_device_name');
          }
        } catch (error) {
          logger.warn('[Layer 0] Direct connection failed, will try scan', { error });
          persistentLogger.addLog('BLE', 'warn', 'Layer 0: Failed', { error });
          this.addDebugLog('error', `[Layer 0] ❌ Connection failed: ${error}`);
          localStorage.removeItem('ble_hr_device_id');
          localStorage.removeItem('ble_hr_device_name');
        }
      }
      
      // ==== LAYER 1: Quick Reconnection Scan ====
      if (savedDeviceId) {
        logger.info('🔍 [Layer 1] Attempting quick reconnection to saved device', {
          deviceId: savedDeviceId,
          deviceName: savedDeviceName 
        });
        this.addDebugLog('info', `[Layer 1] 🔄 Reconnecting to: ${savedDeviceName}`);
        
        try {
          this.emitStatus('connecting');
          await BleClient.connect(savedDeviceId, (deviceId) => {
            logger.warn('Saved device disconnected unexpectedly', { deviceId });
            this.attemptReconnect();
          });
          
          // Verify device still has HR service
          const services = await BleClient.getServices(savedDeviceId);
          const hasHRService = services.some(s => 
            s.uuid.toLowerCase() === this.HEART_RATE_SERVICE.toLowerCase()
          );
          
          if (hasHRService) {
            this.bleDevice = { deviceId: savedDeviceId, name: savedDeviceName || 'Saved Device' };
            
            // Start notifications
            await BleClient.startNotifications(
              savedDeviceId,
              this.HEART_RATE_SERVICE,
              this.HEART_RATE_CHARACTERISTIC,
              (value) => this.handleBLEHeartRate(value)
            );
            
            this.emitStatus('connected');
            logger.info('✅ [Layer 1] Reconnected successfully', { deviceName: savedDeviceName });
            this.addDebugLog('info', `[Layer 1] ✅ Connected to ${savedDeviceName}`);
            this.trackEvent('ble_sensor_reconnected', { deviceName: savedDeviceName, layer: 1 });
            
            return true;
          }
        } catch (error) {
          logger.warn('❌ [Layer 1] Reconnection failed', { error });
          this.addDebugLog('warn', '[Layer 1] ❌ Reconnection failed, clearing saved device');
          // Clear saved device if reconnection failed
          localStorage.removeItem('ble_hr_device_id');
          localStorage.removeItem('ble_hr_device_name');
        }
      }
      
      // ==== LAYER 2: Filtered Scan (HR Service) ====
      logger.info('🔍 [Layer 2] Starting filtered BLE scan for HR devices');
      this.addDebugLog('info', '[Layer 2] 🔍 Starting scan with HR service filter');
      this.discoveredDevices = [];
      
      try {
        await BleClient.requestLEScan(
          { services: [this.HEART_RATE_SERVICE] },
          (result) => {
            const deviceInfo = `${result.device.name || 'Unknown'} (${result.device.deviceId.substring(0, 8)}...) | RSSI: ${result.rssi}dB`;
            logger.info('📱 [Layer 2] Device found', { 
              name: result.device.name || 'Unknown',
              rssi: result.rssi,
              hasHRService: true
            });
            this.addDebugLog('info', `[Layer 2] 📱 Found: ${deviceInfo}`);
            
            // Avoid duplicates
            if (!this.discoveredDevices.some(d => d.deviceId === result.device.deviceId)) {
              const discoveredDevice: DiscoveredBLEDevice = {
                ...result.device,
                rssi: result.rssi,
                hasHRService: true,
                isSamsung: this.isSamsungDevice(result.device.name || '')
              };
              this.discoveredDevices.push(discoveredDevice);
              this.emitDevicesFound([...this.discoveredDevices]);
            }
          }
        );
        
        // Scan for 10 seconds
        await new Promise(resolve => setTimeout(resolve, 10000));
        await BleClient.stopLEScan();
        
        logger.info('📊 [Layer 2] Scan completed', { devicesFound: this.discoveredDevices.length });
        this.addDebugLog('info', `[Layer 2] 📊 Scan complete: ${this.discoveredDevices.length} devices`);
        
        // Emit final list of Layer 2 devices
        this.emitStatus('devices_found_layer2');
        
        if (this.discoveredDevices.length > 0) {
          // Wait for user selection - don't auto-connect
          logger.info('[Layer 2] Waiting for user to select device');
          return false; // Don't auto-connect, let user choose
        } else {
          logger.warn('❌ [Layer 2] No HR devices found');
          this.addDebugLog('warn', '[Layer 2] ❌ No devices found in 10s scan');
        }
      } catch (scanError) {
        logger.warn('❌ [Layer 2] Scan failed', { error: scanError });
        this.addDebugLog('error', `[Layer 2] ❌ Scan error: ${scanError}`);
      }
      
      // ==== LAYER 3: General Scan (No Filter) - Show ALL BLE Devices ====
      logger.info('🔍 [Layer 3] Starting general scan (all BLE devices)');
      this.addDebugLog('info', '[Layer 3] 🔍 Starting unfiltered scan - 30s');
      
      try {
        await BleClient.requestLEScan(
          {}, // No service filter - scan ALL BLE devices!
          (result) => {
            // Show ALL devices, let user choose
            const deviceInfo = `${result.device.name || 'Unknown Device'} | RSSI: ${result.rssi}dB`;
            logger.info('📱 [Layer 3] Device found', { 
              name: result.device.name || 'Unknown',
              deviceId: result.device.deviceId.substring(0, 8),
              rssi: result.rssi
            });
            this.addDebugLog('info', `[Layer 3] 📱 Found: ${deviceInfo}`);
            
            // Add device if not already in list (avoid duplicates)
            if (!this.discoveredDevices.some(d => d.deviceId === result.device.deviceId)) {
              const name = (result.device.name || '').toLowerCase();
              
              // Detect if device is likely a wearable/fitness tracker
              const isWearable = name.includes('gear') || name.includes('galaxy') || 
                                name.includes('watch') || name.includes('band') ||
                                name.includes('polar') || name.includes('garmin') ||
                                name.includes('suunto') || name.includes('samsung') ||
                                name.includes('sm-') || name.includes('fit') || 
                                name.includes('active') || name.includes('apple');
              
              const discoveredDevice: DiscoveredBLEDevice = {
                ...result.device,
                rssi: result.rssi,
                hasHRService: undefined, // Unknown until connected
                isSamsung: this.isSamsungDevice(result.device.name || ''),
                isWearable // New flag for UI
              };
              this.discoveredDevices.push(discoveredDevice);
              this.emitDevicesFound([...this.discoveredDevices]);
            }
          }
        );
        
        // ✅ Increased to 30 seconds for Samsung devices
        await new Promise(resolve => setTimeout(resolve, 30000));
        await BleClient.stopLEScan();
        
        logger.info('📊 [Layer 3] Scan completed', { devicesFound: this.discoveredDevices.length });
        this.addDebugLog('info', `[Layer 3] 📊 Scan complete: ${this.discoveredDevices.length} devices`);
        
        // Emit status that devices were found - wait for user selection
        if (this.discoveredDevices.length > 0) {
          this.emitStatus('devices_found_layer3');
          logger.info('[Layer 3] Waiting for user to select device');
          return false; // Don't auto-connect, let user choose
        }
      } catch (scanError) {
        logger.warn('❌ [Layer 3] Scan failed', { error: scanError });
        this.addDebugLog('error', `[Layer 3] ❌ Scan error: ${scanError}`);
      }
      
      // ==== LAYER 4: HealthKit Fallback (iOS Only) ====
      const platform = Capacitor.getPlatform();
      if (platform === 'ios' && this.healthKitAvailable) {
        logger.info('🔍 [Layer 4] Checking HealthKit fallback');
        this.addDebugLog('info', '[Layer 4] 🔍 Checking HealthKit for recent HR');
        
        try {
          // Check if there's recent HR data in HealthKit
          const now = new Date();
          const oneMinuteAgo = new Date(now.getTime() - 60000);
          
          const result: QueryOutput<any> = await CapacitorHealthkit.queryHKitSampleType({
            sampleName: 'HKQuantityTypeIdentifierHeartRate' as SampleNames,
            startDate: oneMinuteAgo.toISOString(),
            endDate: now.toISOString(),
            limit: 1
          });
          
          if (result.resultData && result.resultData.length > 0) {
            logger.info('✅ [Layer 4] HealthKit data available');
            this.addDebugLog('info', '[Layer 4] ✅ Found recent HR in HealthKit');
            this.emitStatus('healthkit_available');
            
            // Store that we're using HealthKit
            localStorage.setItem('bpm_source', 'healthkit_fallback');
            
            this.trackEvent('ble_fallback_healthkit', { hasRecentData: true });
            
            // Start HealthKit monitoring
            await this.startHealthPlatformMonitoring();
            
            return true;
          } else {
            logger.warn('❌ [Layer 4] No recent HR in HealthKit');
            this.addDebugLog('warn', '[Layer 4] ❌ No recent HR data');
            this.emitStatus('error_samsung');
          }
        } catch (error) {
          logger.warn('❌ [Layer 4] HealthKit check failed', { error });
          this.addDebugLog('error', `[Layer 4] ❌ HealthKit error: ${error}`);
        }
      }
      
      // All layers failed
      logger.warn('All BLE discovery layers failed - no HR monitor found');
      this.addDebugLog('error', '❌ All discovery layers failed - no HR monitor found');
      this.emitStatus('error_no_device');
      throw new Error('No heart rate monitor found');
      
    } catch (error) {
      logger.warn('BLE connection failed', error);
      this.emitStatus('error_samsung');
      
      return false;
    }
  }

  /**
   * Check if device name matches Samsung patterns
   */
  private isSamsungDevice(name: string): boolean {
    const lowerName = name.toLowerCase();
    return lowerName.includes('gear') || 
           lowerName.includes('galaxy') || 
           lowerName.includes('watch') ||
           lowerName.includes('samsung') ||
           lowerName.includes('sm-') ||
           lowerName.includes('fit') ||
           lowerName.includes('active');
  }

  private async connectToDevice(device: BleDevice): Promise<boolean> {
    try {
      this.emitStatus('connecting');
      await BleClient.connect(device.deviceId, (deviceId) => {
        logger.warn('Device disconnected unexpectedly', { deviceId });
        this.attemptReconnect();
      });
      
      this.bleDevice = device;
      
      // Save for future reconnection
      localStorage.setItem('ble_hr_device_id', device.deviceId);
      localStorage.setItem('ble_hr_device_name', device.name || 'HR Monitor');
      
      // Start notifications
      await BleClient.startNotifications(
        device.deviceId,
        this.HEART_RATE_SERVICE,
        this.HEART_RATE_CHARACTERISTIC,
        (value) => this.handleBLEHeartRate(value)
      );
      
      this.emitStatus('connected');
      logger.info('BLE heart rate sensor connected', { deviceName: device.name });
      
      return true;
    } catch (error) {
      logger.error('Failed to connect to device', error);
      return false;
    }
  }

  private async disconnectBLESensor(): Promise<void> {
    if (!this.bleDevice) return;

    try {
      await BleClient.stopNotifications(
        this.bleDevice.deviceId,
        this.HEART_RATE_SERVICE,
        this.HEART_RATE_CHARACTERISTIC
      );

      await BleClient.disconnect(this.bleDevice.deviceId);
      this.bleDevice = null;
      
      // Clear reconnection attempts
      this.reconnectAttempts = 0;
      if (this.reconnectTimeout) {
        clearTimeout(this.reconnectTimeout);
        this.reconnectTimeout = null;
      }
      
      this.emitStatus('disconnected');
      this.trackEvent('ble_sensor_disconnected');
    } catch (error) {
      logger.error('BLE disconnection error', error);
    }
  }

  /**
   * Attempt automatic reconnection to BLE sensor
   */
  private async attemptReconnect(): Promise<void> {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      logger.warn('Max reconnection attempts reached', { 
        attempts: this.reconnectAttempts 
      });
      this.emitStatus('error');
      if (this.connectionLostCallback) {
        this.connectionLostCallback();
      }
      return;
    }

    this.reconnectAttempts++;
    logger.info('Attempting BLE reconnection', { 
      attempt: this.reconnectAttempts,
      maxAttempts: this.maxReconnectAttempts 
    });

    this.emitStatus('reconnecting');

    // Exponential backoff: 2s, 4s, 8s, 16s, 32s
    const backoffMs = Math.min(2000 * Math.pow(2, this.reconnectAttempts - 1), 32000);

    this.reconnectTimeout = setTimeout(async () => {
      try {
        const success = await this.connectBLESensor();
        if (success) {
          logger.info('BLE reconnection successful', { 
            attempts: this.reconnectAttempts 
          });
          this.reconnectAttempts = 0;
          this.trackEvent('ble_reconnection_success', { 
            attempts: this.reconnectAttempts 
          });
        } else {
          // Try again
          await this.attemptReconnect();
        }
      } catch (error) {
        logger.error('BLE reconnection failed', error, { 
          attempt: this.reconnectAttempts 
        });
        await this.attemptReconnect();
      }
    }, backoffMs);
  }

  private handleBLEHeartRate(value: DataView): void {
    try {
      // Validate minimum packet size
      if (value.byteLength < 2) {
        logger.warn('BLE packet too small, ignoring', { byteLength: value.byteLength });
        return;
      }

      // Parse Heart Rate Measurement (standard BLE format)
      // First byte is flags, second byte is BPM
      const flags = value.getUint8(0);
      const is16Bit = (flags & 0x01) !== 0;
      
      let bpm: number;
      if (is16Bit) {
        if (value.byteLength < 3) {
          logger.warn('BLE packet too small for 16-bit HR', { byteLength: value.byteLength });
          return;
        }
        bpm = value.getUint16(1, true); // little-endian
      } else {
        bpm = value.getUint8(1);
      }

      this.addReading({
        bpm,
        timestamp: Date.now(),
        source: 'ble',
        deviceId: this.bleDevice?.deviceId
      });
    } catch (error) {
      logger.error('Failed to parse BLE heart rate data', error);
    }
  }

  private async requestHealthKitAuthorization(): Promise<boolean> {
    try {
      await CapacitorHealthkit.requestAuthorization({
        all: [],
        read: ['HKQuantityTypeIdentifierHeartRate'],
        write: ['HKQuantityTypeIdentifierHeartRate']
      });
      
      logger.info('HealthKit authorization granted');
      this.addDebugLog('info', '✅ HealthKit authorization granted');
      return true;
    } catch (error: any) {
      const errorDetails = {
        name: error?.name || 'Unknown',
        message: error?.message || String(error),
        code: error?.code
      };
      
      logger.error('HealthKit authorization denied', errorDetails);
      this.addDebugLog('error', `❌ HealthKit authorization denied: ${errorDetails.message}`, errorDetails);
      persistentLogger.addLog('HEALTHKIT', 'error', 'HealthKit authorization denied', errorDetails);
      
      return false;
    }
  }

  private async startHealthPlatformMonitoring(): Promise<boolean> {
    const platform = Capacitor.getPlatform();

    if (platform === 'ios') {
      // Revalidate HealthKit availability (important for tests that change platform)
      try {
        const availabilityCheck = await CapacitorHealthkit.isAvailable();
        this.healthKitAvailable = availabilityCheck.isAvailable;
      } catch (error: any) {
        const errorDetails = {
          name: error?.name || 'Unknown',
          message: error?.message || String(error)
        };
        logger.warn('HealthKit availability recheck failed', errorDetails);
        this.addDebugLog('warn', `HealthKit recheck failed: ${errorDetails.message}`, errorDetails);
        this.healthKitAvailable = false;
      }

      if (!this.healthKitAvailable) {
        logger.info('HealthKit not available, will fallback to BLE');
        this.addDebugLog('info', 'ℹ️ HealthKit not available - will use BLE monitoring');
        return false;
      }

      // Request authorization first
      const authorized = await this.requestHealthKitAuthorization();
      if (!authorized) {
        logger.info('HealthKit authorization denied, will fallback to BLE');
        this.addDebugLog('warn', '⚠️ HealthKit auth denied - falling back to BLE');
        // Don't emit error status - let BLE try
        return false;
      }
      
      this.emitStatus('connected');
      
      // Poll every 2 seconds for latest reading
      this.healthKitInterval = setInterval(async () => {
        try {
          const now = new Date();
          const fiveSecondsAgo = new Date(now.getTime() - 5000);
          
          const result: QueryOutput<any> = await CapacitorHealthkit.queryHKitSampleType({
            sampleName: 'HKQuantityTypeIdentifierHeartRate' as SampleNames,
            startDate: fiveSecondsAgo.toISOString(),
            endDate: now.toISOString(),
            limit: 1
          });
          
          if (result.resultData && result.resultData.length > 0) {
            const latestReading = result.resultData[0];
            const bpm = Math.round(latestReading.quantity);
            
            this.addReading({ 
              bpm, 
              timestamp: new Date(latestReading.endDate).getTime(), 
              source: 'healthkit' 
            });
          }
        } catch (error) {
          logger.warn('Failed to query HealthKit', error);
        }
      }, 2000); // Update every 2 seconds
      
      logger.info('HealthKit monitoring started');
      return true;
    } else if (platform === 'android' && this.googleFitAvailable) {
      // TODO: Start Google Fit monitoring
      // await GoogleFit.startHeartRateMonitoring((bpm) => {
      //   this.addReading({ bpm, timestamp: Date.now(), source: 'googlefit' });
      // });
      return false;
    }
    
    return false;
  }

  private async stopHealthPlatformMonitoring(): Promise<void> {
    const platform = Capacitor.getPlatform();

    if (platform === 'ios' && this.healthKitAvailable) {
      if (this.healthKitInterval) {
        clearInterval(this.healthKitInterval);
        this.healthKitInterval = null;
      }
      
      this.emitStatus('disconnected');
      logger.info('HealthKit monitoring stopped');
    } else if (platform === 'android' && this.googleFitAvailable) {
      // TODO: Stop Google Fit monitoring
      // await GoogleFit.stopHeartRateMonitoring();
    }
  }

  addManualReading(bpm: number): void {
    this.addReading({
      bpm,
      timestamp: Date.now(),
      source: 'manual'
    });
  }

  private addReading(reading: BPMReading): void {
    this.currentBPM = reading.bpm;
    this.readings.push(reading);

    // Trigger callbacks or events
    this.onBPMUpdate(reading);

    // Trigger real-time callbacks
    this.bpmCallbacks.forEach(cb => cb(reading));

    this.trackEvent('bpm_reading', {
      bpm: reading.bpm,
      source: reading.source
    });
  }

  private onBPMUpdate(reading: BPMReading): void {
    // Override this in consumers
    logger.debug('BPM Update', { bpm: reading.bpm, source: reading.source });
  }

  /**
   * Get BLE device connection health status
   */
  getConnectionHealth(): {
    isConnected: boolean;
    device: string | null;
    source: 'ble' | 'healthkit' | 'googlefit' | 'none';
    reconnectAttempts: number;
    lastReadingAge: number | null;
  } {
    const lastReading = this.readings[this.readings.length - 1];
    return {
      isConnected: this.bleDevice !== null || this.healthKitInterval !== null,
      device: this.bleDevice?.name || null,
      source: this.bleDevice ? 'ble' : 
              this.healthKitAvailable && this.healthKitInterval ? 'healthkit' :
              this.googleFitAvailable ? 'googlefit' : 'none',
      reconnectAttempts: this.reconnectAttempts,
      lastReadingAge: lastReading ? Date.now() - lastReading.timestamp : null
    };
  }

  getCurrentBPM(): number {
    return this.currentBPM;
  }

  getAllReadings(): BPMReading[] {
    return [...this.readings];
  }

  getAverageBPM(): number {
    if (this.readings.length === 0) return 0;
    const sum = this.readings.reduce((acc, r) => acc + r.bpm, 0);
    return Math.round(sum / this.readings.length);
  }

  getMaxBPM(): number {
    if (this.readings.length === 0) return 0;
    return Math.max(...this.readings.map(r => r.bpm));
  }

  getMinBPM(): number {
    if (this.readings.length === 0) return 0;
    return Math.min(...this.readings.map(r => r.bpm));
  }

  getBPMZone(bpm: number, age: number): BPMZone {
    const maxHR = 220 - age;

    const zones: BPMZone[] = [
      { name: 'Rest', min: 0, max: maxHR * 0.5, color: '#808080' },
      { name: 'Warm-up', min: maxHR * 0.5, max: maxHR * 0.6, color: '#4CAF50' },
      { name: 'Fat Burn', min: maxHR * 0.6, max: maxHR * 0.7, color: '#2196F3' },
      { name: 'Cardio', min: maxHR * 0.7, max: maxHR * 0.8, color: '#FF9800' },
      { name: 'Peak', min: maxHR * 0.8, max: maxHR, color: '#F44336' }
    ];

    return zones.find(z => bpm >= z.min && bpm < z.max) || zones[0];
  }

  private trackEvent(event: string, data?: any): void {
    logger.debug('BPMService Event', { event, ...data });
    
    if (typeof window !== 'undefined' && (window as any).analytics) {
      (window as any).analytics.track(event, data);
    }
  }
}

export const bpmService = new BPMService();
export type { BPMReading, BPMZone };
